// Sprint 6.7 — composer `/` + `@` autocomplete dropdown.
//
// Renders grouped, keyboard-navigable results above the composer textarea.
// Tokens follow design-system §9.X exactly (bg-popover, rounded-md,
// shadow-lg, 36px desktop rows / 44px mobile, caption mono name + sans desc).
//
// Parent wires this to `useAutocomplete` — this component stays presentational:
// accepts `trigger`, `results`, `focusedIndex`, `onSelect`, `onHoverIndex`,
// `onClose`. No fetching, no global state.
//
// DS.6 (sprint aos-ds-composer, §2 D3, §0.19) — stays a PRODUCT BLOCK and the
// reason is measured, not aesthetic: the DS has no caret-anchored inline
// completer. `MentionPicker` steals focus on mount (its own search `<input>` +
// a focus effect) and is button-opened; `Combobox` REPLACES the textarea and
// portals. Both would break the one thing this palette is: a list anchored to
// the caret INSIDE a textarea the founder never leaves. What DS.6 does change
// is the SKIN — the panel's surface, border, radius, shadow and rows are skin
// roles now, and the two `arb-px` Gate AA rows this file carried drain with
// them: the arbitrary 560 px panel width becomes the named
// `max-w-autocomplete` (35rem = 560 px exactly, web/tailwind.config.js) and
// the arbitrary 36 px desktop row floor becomes `md:min-h-9`, the stock
// Tailwind 2.25rem step. No pixel moves; the gate's grep no longer has a
// bracketed value to count.

import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from '../i18n';
import { Icon, type IconName } from './atoms/Icon';
import type { WorkdirIndexCategory, WorkdirIndexEntry } from '../types';

const CATEGORY_ORDER_SLASH: WorkdirIndexCategory[] = [
  'skill',
  'custom-command',
  'command',
];
const CATEGORY_ORDER_AT: WorkdirIndexCategory[] = [
  'agent',
  'rule',
  'doc',
  'file',
];

const CATEGORY_LABEL: Record<WorkdirIndexCategory, string> = {
  skill: 'Skills',
  command: 'Commands',
  'custom-command': 'Custom',
  agent: 'Agents',
  rule: 'Rules',
  doc: 'Docs',
  file: 'Files',
};

const CATEGORY_ICON: Record<WorkdirIndexCategory, IconName> = {
  skill: 'Zap',
  command: 'Terminal',
  'custom-command': 'Terminal',
  agent: 'Bot',
  rule: 'ScrollText',
  doc: 'FileText',
  file: 'Folder',
};

export interface AutocompleteDropdownProps {
  trigger: '@' | '/';
  query: string;
  results: WorkdirIndexEntry[];
  focusedIndex: number;
  loading: boolean;
  error: string | null;
  onSelect: (entry: WorkdirIndexEntry) => void;
  onHoverIndex: (index: number) => void;
  onClose: () => void;
  // Optional test seam: skip mouseDown outside-close so RTL tests don't
  // have to worry about ordering.
  disableOutsideClickClose?: boolean;
}

export function AutocompleteDropdown({
  trigger,
  query,
  results,
  focusedIndex,
  loading,
  error,
  onSelect,
  onHoverIndex,
  onClose,
  disableOutsideClickClose = false,
}: AutocompleteDropdownProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click (mousedown so it fires before focus shifts).
  useEffect(() => {
    if (disableOutsideClickClose) return;
    function handleMouseDown(e: MouseEvent) {
      const el = containerRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      onClose();
    }
    window.addEventListener('mousedown', handleMouseDown);
    return () => window.removeEventListener('mousedown', handleMouseDown);
  }, [onClose, disableOutsideClickClose]);

  const grouped = useMemo(() => {
    const order = trigger === '/' ? CATEGORY_ORDER_SLASH : CATEGORY_ORDER_AT;
    const byCat = new Map<WorkdirIndexCategory, { entry: WorkdirIndexEntry; globalIndex: number }[]>();
    results.forEach((entry, globalIndex) => {
      const list = byCat.get(entry.category) ?? [];
      list.push({ entry, globalIndex });
      byCat.set(entry.category, list);
    });
    return order
      .map((cat) => ({ category: cat, items: byCat.get(cat) ?? [] }))
      .filter((section) => section.items.length > 0);
  }, [results, trigger]);

  // Totally-empty workdir → show the special message (Phase 5).
  const isTrulyEmpty =
    !loading && !error && results.length === 0 && query.length === 0;
  const hasNoMatchesForQuery =
    !loading && !error && results.length === 0 && query.length > 0;

  return (
    <div
      ref={containerRef}
      role="listbox"
      aria-label={
        trigger === '/' ? 'Slash command suggestions' : 'Mention suggestions'
      }
      data-testid="autocomplete-dropdown"
      data-trigger={trigger}
      className="
        absolute bottom-full mb-2 left-0
        w-full max-w-autocomplete
        max-h-[min(60vh,480px)] overflow-y-auto
        z-30
        animate-fade-in
      "
      style={{
        background: 'var(--bg-grey-lightest)',
        border: 'var(--border-width-default) solid var(--border-strong)',
        borderRadius: 'var(--radius-s)',
        boxShadow: 'var(--shadow-lg)',
      }}
    >
      {loading && results.length === 0 && (
        <div
          className="px-3 py-4 text-body text-muted-foreground text-center"
          data-testid="autocomplete-loading"
        >
          {t('autocomplete.loading')}
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="px-3 py-3 text-body text-status-error"
          data-testid="autocomplete-error"
        >
          {error}
        </div>
      )}

      {isTrulyEmpty && (
        <div
          className="px-3 py-4 text-body text-muted-foreground text-center"
          data-testid="autocomplete-empty-workdir"
        >
          {t('autocomplete.noResults')}
        </div>
      )}

      {hasNoMatchesForQuery && (
        <div
          className="px-3 py-4 text-body text-muted-foreground text-center"
          data-testid="autocomplete-no-matches"
        >
          {t('autocomplete.noResults')}
        </div>
      )}

      {grouped.map((section) => (
        <div key={section.category}>
          <div className="text-label text-muted-foreground uppercase px-3 py-2">
            {CATEGORY_LABEL[section.category]}
          </div>
          {section.items.map(({ entry, globalIndex }) => (
            <AutocompleteRow
              key={`${entry.category}:${entry.insertText}:${entry.path}`}
              entry={entry}
              index={globalIndex}
              focused={focusedIndex === globalIndex}
              onSelect={onSelect}
              onHoverIndex={onHoverIndex}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

interface RowProps {
  entry: WorkdirIndexEntry;
  index: number;
  focused: boolean;
  onSelect: (entry: WorkdirIndexEntry) => void;
  onHoverIndex: (index: number) => void;
}

function AutocompleteRow({ entry, index, focused, onSelect, onHoverIndex }: RowProps) {
  const iconName: IconName = CATEGORY_ICON[entry.category] ?? 'Sparkles';
  return (
    <button
      type="button"
      role="option"
      aria-selected={focused}
      tabIndex={-1}
      data-testid="autocomplete-row"
      data-focused={focused ? 'true' : 'false'}
      data-category={entry.category}
      // onMouseDown instead of onClick so we fire before the textarea blur
      // strips focus + kills the dropdown. We preventDefault to keep the
      // caret inside the textarea.
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect(entry);
      }}
      onMouseEnter={() => onHoverIndex(index)}
      className={`
        w-full flex items-center gap-3
        px-3 py-2 md:min-h-9 min-h-touch-target
        text-left text-body
        transition-colors duration-100
        ${focused ? 'bg-accent' : 'hover:bg-accent'}
      `}
    >
      <Icon name={iconName} size={16} className="text-muted-foreground shrink-0" />
      <span className="font-mono text-foreground shrink-0 truncate max-w-[40%]">
        {entry.display}
      </span>
      {entry.description && (
        <span className="text-muted-foreground truncate flex-1">
          {entry.description}
        </span>
      )}
    </button>
  );
}
